import { useMemo, useCallback, useEffect } from 'react';
import {
  ConversationAssignee,
  ConversationData,
  dispatch,
  ErrorVariants,
  getWorkspaceId,
  showToast,
  UpdateConversationAssigneeType,
  useAppSelector,
  useLazyGetTeamSearchQuery,
  useRemoveAssigneeMutation,
  UserType,
  useUpdateAssigneeMutation,
} from '@zonofi/zono-money-store';
import { useDebounceSearch } from '@zonofi/seller-hub/hooks';

interface AssignTeamMembersPayload {
  assigned: ConversationAssignee[];
  open: boolean;
  updateConversations: (item: ConversationData) => void;
}

export const useAssignTeamMember = ({
  assigned,
  open,
  updateConversations,
}: AssignTeamMembersPayload) => {
  const [fetchTeamMembers, teamMembersResponse] = useLazyGetTeamSearchQuery();
  const workspaceId = useAppSelector(getWorkspaceId);
  const [updateAssignee, updateAssigneeResponse] = useUpdateAssigneeMutation();
  const [removeAssignee, removeAssigneeResponse] = useRemoveAssigneeMutation();

  const { debouncedSearchValue } = useDebounceSearch();
  const assignedTeamMembers = useMemo(
    () => assigned.map(({ teamMemberId }) => teamMemberId),
    [assigned]
  );

  const getTeamMembers = useCallback(() => {
    fetchTeamMembers({
      sellerWorkspaceId: workspaceId,
      page: 1,
      pageSize: 50,
      includeInactive: false,
      includePendingInvites: false,
      includeTeamCustomersMapping: false,
      userType: [UserType.CFA, UserType.MEMBER, UserType.OWNER],
      ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
    });
  }, [debouncedSearchValue, workspaceId]);

  useEffect(() => {
    if (open) getTeamMembers();
  }, [open, debouncedSearchValue, workspaceId]);

  const teamMemberData = useMemo(
    () =>
      teamMembersResponse.data?.data.filter(
        (x) => !assignedTeamMembers.includes(x.id)
      ) ?? [],
    [teamMembersResponse.data, assignedTeamMembers]
  );

  const onUpdateAssignee = ({
    teamMemberId,
    conversationId,
  }: {
    teamMemberId: string;
    conversationId: string;
  }) => {
    updateAssignee({
      conversationId,
      sellerWorkspaceId: workspaceId,
      teamMemberId,
      type: UpdateConversationAssigneeType.ASSIGN,
    })?.then(() => {
      dispatch(
        showToast({
          canDisplay: true,
          message: 'Assigned team member successfully',
          type: ErrorVariants.SUCCESS,
          responseStatus: 'success',
        })
      );
    });
  };

  const onRemoveAssignee = ({
    teamMemberId,
    conversationId,
  }: {
    teamMemberId: string;
    conversationId: string;
  }) => {
    removeAssignee({
      conversationId,
      sellerWorkspaceId: workspaceId,
      teamMemberId,
    })?.then(() => {
      dispatch(
        showToast({
          canDisplay: true,
          message: 'Assigned team member removed successfully',
          type: ErrorVariants.SUCCESS,
          responseStatus: 'success',
        })
      );
    });
  };

  useEffect(() => {
    if (
      (updateAssigneeResponse.isSuccess && updateAssigneeResponse.data?.data) ||
      (removeAssigneeResponse.isSuccess && removeAssigneeResponse.data?.data)
    ) {
      const updatedConversation =
        updateAssigneeResponse.data?.data ?? removeAssigneeResponse.data?.data;
      if (updatedConversation) updateConversations(updatedConversation);
      updateAssigneeResponse.reset();
      removeAssigneeResponse.reset();
    }
  }, [updateAssigneeResponse, removeAssigneeResponse]);

  return {
    teamMemberData,
    onUpdateAssignee,
    onRemoveAssignee,
  };
};
